/**
 * The owner's view of a published app: who is using it, what they have sent, and how to
 * reach them.
 *
 * Thin wrappers over the API, web-owned. The shared versions do the same work but fetch
 * their token from CognitoService, which since SEC-14 stores the refresh token in
 * expo-secure-store and therefore pulls React Native into anything that imports it. These
 * take the token from the one request() instead, which is where it already lives.
 *
 * Pure helpers — sortNewestFirst, submissionsToCsv — are still imported from the shared
 * modules where they are pure; only the fetching moved. The words for People (lastSeen and
 * the sign-in summary) are in state/people.ts.
 */
import { ApiError, request } from './client';

export interface SubmissionRow {
  [field: string]: unknown;
}

export interface SubmissionPage {
  rows: SubmissionRow[];
  /** Pass back to read the next page; absent when there are no more. */
  cursor?: string;
}

const PAGE = 200;

/** Newest first. Applied to everything accumulated, not one page at a time — rows are keyed
 *  by a random id, so the server returns them in no useful order and per-page sorting would
 *  interleave old replies among new ones on every "load more". */
export function sortNewestFirst(rows: SubmissionRow[]): SubmissionRow[] {
  return [...rows].sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')));
}

export async function fetchSubmissions(
  appId: string | undefined,
  collection: string,
  cursor?: string,
): Promise<SubmissionPage> {
  if (!appId) throw new ApiError('misconfigured', 'This app has not been published yet.');
  const query = `?limit=${PAGE}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  const body = await request<{ items?: SubmissionRow[]; cursor?: string }>(
    `/apps/${encodeURIComponent(appId)}/${encodeURIComponent(collection)}${query}`,
  );
  return { rows: sortNewestFirst(body.items ?? []), cursor: body.cursor };
}

/** A spreadsheet of what people sent. Quotes every field: a reply containing a comma or a
 *  newline would otherwise silently shift every column after it. */
export function submissionsToCsv(rows: SubmissionRow[]): string {
  if (rows.length === 0) return '';
  const columns = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const cell = (v: unknown): string => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [columns.map(cell).join(','), ...rows.map((r) => columns.map((c) => cell(r[c])).join(','))].join('\n');
}

export interface AppUser {
  userId: string;
  email?: string;
  role: string;
  joinedAt?: string;
  lastActiveAt?: string;
}

/**
 * Who is using a published app, most recently active first.
 *
 * `/members`, as the phone reads it. This called `/apps/{id}/users`, a route the API does not
 * have: API Gateway matched it to the `/apps/{appId}/{entity}` DATA route instead, which looked
 * for a collection called "users" and answered 404 "Entity 'users' not found" — so People
 * failed for every real app, on the web only, while every unit test mocked the call and passed.
 */
export async function fetchAppUsers(appId: string | undefined): Promise<AppUser[]> {
  if (!appId) throw new ApiError('misconfigured', 'This app has not been published yet.');
  const body = await request<{ items?: AppUser[] }>(`/apps/${encodeURIComponent(appId)}/members`);
  return (body.items ?? [])
    .slice()
    .sort((a, b) => String(b.lastActiveAt ?? b.joinedAt ?? '').localeCompare(String(a.lastActiveAt ?? a.joinedAt ?? '')));
}

/**
 * Remove someone from a published app, and the data it holds about them with them.
 *
 * Not the same as blocking: that is `setAppUserRole(..., 'blocked')`, which keeps their data.
 */
export async function removeAppUser(appId: string, userId: string): Promise<{ records: number }> {
  const body = await request<{ records?: number } | undefined>(
    `/apps/${encodeURIComponent(appId)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
  return { records: body?.records ?? 0 };
}

/** Block someone (or let them back in) without touching what they saved. The phone's contract. */
export async function setAppUserRole(appId: string, userId: string, role: 'user' | 'blocked'): Promise<void> {
  await request(`/apps/${encodeURIComponent(appId)}/members/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    body: { role },
  });
}

export interface BroadcastResult {
  /** Confirmed subscribers the send was attempted to. */
  recipients: number;
  /** How many the mail provider accepted. */
  sent: number;
}

export async function sendBroadcast(
  appId: string | undefined,
  message: { subject: string; body: string; senderAddress: string },
): Promise<BroadcastResult> {
  if (!appId) throw new ApiError('misconfigured', 'This app has not been published yet.');
  return request<BroadcastResult>(`/apps/${encodeURIComponent(appId)}/broadcast`, {
    method: 'POST',
    body: message,
  });
}

export interface AddressChange {
  slug: string;
  siteUrl?: string;
  /** Earlier addresses that still open this app. */
  keptWorking: string[];
}

/**
 * Move a published app to an address its owner chose. The old address keeps working:
 * somebody has that link, and a rename they never hear about is no reason for it to stop
 * opening.
 */
export async function changeSiteAddress(appId: string | undefined, desired: string): Promise<AddressChange> {
  if (!appId) throw new ApiError('misconfigured', 'This app has not been published yet.');
  const body = await request<{ slug?: string; siteUrl?: string; keptWorking?: string[] }>(
    `/apps/${encodeURIComponent(appId)}/web/address`,
    { method: 'PUT', body: { slug: desired } },
  );
  if (!body.slug) throw new ApiError('server', 'The server did not confirm the new address.');
  return { slug: body.slug, siteUrl: body.siteUrl, keptWorking: body.keptWorking ?? [] };
}
